import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { DAILY_MOOD_LIST, MOOD_GRADIENTS, getDailyMood } from "./dailyMoods";
import { useDailyMood } from "./DailyMoodContext";
import { THEMES, getThemeById } from "../recommend/themes";

const screenEnter = keyframes`
  from { transform: translateY(100vh); }
  to   { transform: translateY(0); }
`;

const slideInUp = keyframes`
  from { transform: translateY(56px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const slideOutUp = keyframes`
  from { transform: translateY(0);    opacity: 1; }
  to   { transform: translateY(-40px); opacity: 0; }
`;

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';

export default function DailyMoodScreen({ onClose, onComplete }) {
  const { setTodayMood, setTotonoe } = useDailyMood();
  const [step, setStep] = useState(1);
  const [exiting, setExiting] = useState(false);
  const [pendingMoodId, setPendingMoodId] = useState(null);

  const handleMoodSelect = (id) => {
    setTodayMood(id);
    setPendingMoodId(id);
    // Step1を上へ退場させてからStep2を入場
    setExiting(true);
    setTimeout(() => {
      setStep(2);
      setExiting(false);
    }, 260);
  };

  const handleTotonoeSelect = (themeId) => {
    setTotonoe(themeId);
    onComplete ? onComplete() : onClose?.();
  };

  const selectedMood = pendingMoodId ? getDailyMood(pendingMoodId) : null;
  const suggestedIds = selectedMood?.suggestedThemes || [];
  const suggestedThemes = suggestedIds.map((id) => getThemeById(id)).filter(Boolean);
  const otherThemes = THEMES.filter((t) => !suggestedIds.includes(t.id));

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "#18140f",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        overflowY: "auto",
        // 画面全体が下からにゅーっと
        animation: `${screenEnter} 0.5s cubic-bezier(0.22, 1, 0.36, 1)`,
      }}
    >
      {/* ステップコンテンツ：key変更でスライドイン、exiting中はスライドアウト */}
      <Box
        key={step}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          animation: exiting
            ? `${slideOutUp} 0.24s ease-in forwards`
            : `${slideInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {step === 1 ? (
          <Step1 onSelect={handleMoodSelect} onSkip={() => onClose?.()} />
        ) : (
          <Step2
            mood={selectedMood}
            suggestedThemes={suggestedThemes}
            otherThemes={otherThemes}
            onSelect={handleTotonoeSelect}
            onSkip={() => onClose?.()}
          />
        )}
      </Box>
    </Box>
  );
}

function Step1({ onSelect, onSkip }) {
  return (
    <>
      <Typography variant="h5" sx={{ color: "#e8dfc8", fontWeight: 900, letterSpacing: 4, mb: 0.5, fontFamily: SERIF }}>
        今日の気分は？
      </Typography>
      <Typography variant="body2" sx={{ color: "#a09070", mb: 4, letterSpacing: 1, fontSize: "0.8rem" }}>
        選んだ気分に合わせて画面が変わります
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: { xs: 1, sm: 1.5 },
          width: "100%",
          maxWidth: 540,
          mb: 4,
        }}
      >
        {DAILY_MOOD_LIST.map((m) => (
          <MoodCard key={m.id} mood={m} onClick={() => onSelect(m.id)} />
        ))}
      </Box>

      <Button onClick={onSkip} size="small" sx={{ color: "#605040", fontSize: "0.75rem", letterSpacing: 1 }}>
        あとで選ぶ
      </Button>
    </>
  );
}

function Step2({ mood, suggestedThemes, otherThemes, onSelect, onSkip }) {
  return (
    <>
      {/* 選んだ気分の表示 */}
      {mood && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: "50%",
              background: MOOD_GRADIENTS[mood.id],
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "1.1rem", fontFamily: SERIF, color: mood.id === "oso" ? "#e8e8f0" : "rgba(0,0,0,0.75)", lineHeight: 1 }}>
              {mood.kanji}
            </Typography>
          </Box>
          <Typography sx={{ color: "#c8b890", fontSize: "0.75rem", letterSpacing: 2 }}>
            {mood.reading}・{mood.desc}
          </Typography>
        </Box>
      )}

      <Typography variant="h5" sx={{ color: "#e8dfc8", fontWeight: 900, letterSpacing: 3, mb: 0.5, fontFamily: SERIF }}>
        今日、何を整えたいですか？
      </Typography>
      <Typography variant="body2" sx={{ color: "#a09070", mb: 4, letterSpacing: 1, fontSize: "0.8rem" }}>
        今の気分からのおすすめを★で表示しています
      </Typography>

      {/* おすすめ（2択・大きめ・中央揃え） */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, width: "100%", maxWidth: 440, mb: 2.5 }}>
        {suggestedThemes.map((t) => (
          <Box
            key={t.id}
            onClick={() => onSelect(t.id)}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              cursor: "pointer",
              border: `2px solid ${t.color}80`,
              transition: "transform 0.15s, box-shadow 0.15s",
              "&:hover": { transform: "translateY(-3px)", boxShadow: `0 8px 24px ${t.color}30` },
            }}
          >
            <Box sx={{ bgcolor: t.bg, px: 2, py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.65rem", color: t.color, fontWeight: 800, letterSpacing: 1, mb: 1 }}>
                ★ おすすめ
              </Typography>
              <Typography sx={{ fontSize: "2.2rem", lineHeight: 1, mb: 1 }}>{t.emoji}</Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: t.color }}>
                {t.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* その他（5択・小さめ） */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 1,
          width: "100%",
          maxWidth: 440,
          mb: 4,
        }}
      >
        {otherThemes.map((t) => (
          <Box
            key={t.id}
            onClick={() => onSelect(t.id)}
            sx={{
              borderRadius: 2,
              bgcolor: "#231e17",
              border: "1px solid #3a3028",
              px: 0.5,
              py: 1.5,
              cursor: "pointer",
              textAlign: "center",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "#302820" },
            }}
          >
            <Typography sx={{ fontSize: "1.3rem", lineHeight: 1, mb: 0.5 }}>{t.emoji}</Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#c8b890", lineHeight: 1.3 }}>
              {t.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button onClick={onSkip} size="small" sx={{ color: "#605040", fontSize: "0.75rem", letterSpacing: 1 }}>
        スキップ
      </Button>
    </>
  );
}

function MoodCard({ mood: m, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" },
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      <Box
        sx={{
          background: MOOD_GRADIENTS[m.id],
          height: { xs: 64, sm: 80 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
        }}
      >
        <Typography sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 700, lineHeight: 1, color: m.id === "oso" ? "#e8e8f0" : "rgba(0,0,0,0.75)", fontFamily: SERIF }}>
          {m.kanji}
        </Typography>
        <Typography sx={{ fontSize: "0.55rem", letterSpacing: 2, color: m.id === "oso" ? "rgba(232,232,240,0.7)" : "rgba(0,0,0,0.5)" }}>
          {m.reading}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: "#231e17", px: 0.75, py: 0.75 }}>
        <Typography sx={{ fontSize: "0.55rem", color: "#c8b890", lineHeight: 1.4, textAlign: "center" }}>
          {m.desc}
        </Typography>
      </Box>
    </Box>
  );
}
